const CreditCardFormComponent = require('app/scripts/views/billing/credit-card-form-component.coffee');
const PaidAccount = require('app/scripts/network/paid-account.coffee');
const AjaxPromise = require('app/scripts/network/ajax-promise.coffee');
const Auth = require('app/scripts/db/auth.coffee');

import React from 'react';
import styles from './MoonshotBilling.less';
import classNames from 'classnames';
import { logoDomain } from '@trello/config';
import { useMoonshotBillingQuery } from '@trello/graphql/generated';
import {
  BillingDates,
  OrganizationFeatures as OrgFeatures,
} from '@trello/product-features';
import { Icon, IconName } from 'app/common/components/controls';
import { Noun, Verb, trackUe } from '@trello/analytics';

interface MoonshotBillingProps {
  onFailure: () => void;
  onSkip: () => void;
  onSuccess: () => void;
}

export const MoonshotBilling: React.FC<MoonshotBillingProps> = ({
  onFailure,
  onSkip,
  onSuccess,
}) => {
  const { data } = useMoonshotBillingQuery();
  const org =
    data &&
    data.member &&
    data.member.organizations &&
    data.member.organizations[0];

  const invitedUsersCount =
    org &&
    org.memberships &&
    org.memberships.reduce(
      (count, membership) => (membership.unconfirmed ? count + 1 : count),
      0,
    );

  const onSkipWithTracking = () => {
    onSkip();
    trackUe({
      category: Noun.MOONSHOT,
      verb: Verb.SKIPS,
      directObj: Noun.BUSINESS_CLASS_PAYMENT,
    });
  };
  const orgId = (org && org.id) || '';
  const orgDisplayName = (org && org.displayName) || '';
  const orgLogoHash = (org && org.logoHash) || '';
  const orgImageUrl = [logoDomain, orgLogoHash, '170.png'].join('/');
  const activeBillableMemberCount = (org && org.activeBillableMemberCount) || 0;
  const billableCollaboratorCount = (org && org.billableCollaboratorCount) || 0;
  const billableMemberCount = (org && org.billableMemberCount) || 0;
  const paidAccount = org && org.paidAccount;
  const billingDates = (paidAccount && paidAccount.billingDates) || {};
  const paidAccountPollURL = `/1/organizations/${orgId}?paid_account=true`;
  const createAccountData = {
    id: orgId,
    urlRoot: '/1/organizations',
  };

  const { yearly, monthly, billingStrategy } = OrgFeatures.currentPriceObject(
    activeBillableMemberCount,
    billableCollaboratorCount,
    billableMemberCount,
    billingDates as BillingDates,
  );

  const creditCardFormProps = {
    isMoonshotBilling: true,
    defaultProduct: yearly.productNumber,
    monthlyProduct: monthly,
    yearlyProduct: yearly,
    billingStrategy: billingStrategy,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onSubmit: (submitData: any) => {
      trackUe({
        category: Noun.MOONSHOT,
        verb: Verb.SUBMITS,
        directObj: Noun.BUSINESS_CLASS_PAYMENT,
      });

      return PaidAccount.createAccount(createAccountData, submitData);
    },
    doPoll: () => AjaxPromise({ url: paidAccountPollURL }),
    buttonText: 'Upgrade Team to Business Class',
    extraData: {
      billingContactFullName:
        (paidAccount && paidAccount.contactFullName) ||
        Auth.me().get('fullName'),
      billingContactEmail:
        (paidAccount && paidAccount.contactEmail) || Auth.me().get('email'),
      billingContactLocale:
        (paidAccount && paidAccount.contactLocale) ||
        Auth.me().getPref('locale'),
    },
    // Called on error of form submission
    didntGetAuthStatus: onFailure,
    // Called on success of form submission - should eventually redirect user to success page
    didGetAuthStatus: onSuccess,
  };

  return (
    <div className={classNames(styles.moonshotBillingWrapper, 'billing')}>
      <div className={styles.moonshotBillingHeading}>
        <div className={styles.moonshotBillingOrgImage}>
          {orgLogoHash ? (
            <img src={orgImageUrl} alt={orgDisplayName} />
          ) : (
            <Icon
              name={IconName.Organization}
              className={styles.moonshotBillingOrgIcon}
            />
          )}
        </div>
        <h1 className={styles.moonshotBillingOrgName}>{orgDisplayName}</h1>
      </div>
      <CreditCardFormComponent
        invitedUsersCount={invitedUsersCount}
        {...creditCardFormProps}
      />
      <div className={styles.moonshotBillingCancellationCopy}>
        Not for you? You can cancel at any time.
      </div>
      <div className={styles.moonshotBillingSkip}>
        <div className={styles.moonshotBillingSkipHeading}>
          Not ready for Business Class?
        </div>
        <div className={styles.moonshotBillingSkipDescription}>
          No problem! Get started with up to 10 boards, limited permissions, and
          1 Power-Up per board.
        </div>
        <div
          className={styles.moonshotBillingSkipCTA}
          onClick={onSkipWithTracking}
          role="link"
        >
          Get Started For Free
        </div>
      </div>
    </div>
  );
};
